import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Crosshair, MapPin, Radio, Send } from 'lucide-react'
import { broadcastAlert, publishAlert, type AlertPublishInput, type AlertPublishResponse } from '../../../api/alerts-admin'
import { EmptyState } from '../../../components/common/Status'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { haversineKm } from '../../../lib/geo'
import { S_WAVE_KM_S } from '../../../lib/seismic'
import { hasPermission, parseTokenClaims } from '../../../lib/authz'
import { useAuthStore } from '../../../stores/auth'
import { cn } from '../../../lib/utils'
import { PickMap } from './alert-debug/PickMap'

/** 参考点：S 波到达估算基准（乌鲁木齐） */
const REF_CITY = { name: '乌鲁木齐', lat: 43.8256, lon: 87.6168 }

type Level = 'BE_AWARE' | 'TAKE_ACTION'
type Mode = 'region' | 'broadcast'

/** 原生 range 的仪器化封装（未引入 radix slider 依赖） */
function RangeRow({ label, value, display, min, max, step, onChange }: {
  label: string
  value: number
  display: string
  min: number
  max: number
  step: number
  onChange: (v: number) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <span className="font-mono text-sm tabular-nums text-foreground">{display}</span>
      </div>
      <input
        type="range"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 h-1 w-full cursor-pointer appearance-none rounded-full bg-foreground/10 accent-[var(--primary)]"
      />
    </div>
  )
}

export function AlertDebugSection() {
  const token = useAuthStore((s) => s.token)
  const claims = useMemo(() => parseTokenClaims(token), [token])
  if (!hasPermission(claims, 'alerts.publish')) {
    return (
      <div className="panel-glass rounded-xl">
        <EmptyState
          title="权限不足"
          description="预警下发需要 alerts.publish 权限码（super_admin 旁路）。请联系超级管理员授权。"
        />
      </div>
    )
  }
  return <AlertDebugForm />
}

function AlertDebugForm() {
  const [level, setLevel] = useState<Level>('BE_AWARE')
  const [magnitude, setMagnitude] = useState(5.0)
  const [depth, setDepth] = useState(10)
  const [epicenterName, setEpicenterName] = useState(REF_CITY.name)
  const [lat, setLat] = useState(REF_CITY.lat)
  const [lon, setLon] = useState(REF_CITY.lon)
  const [arrivalSec, setArrivalSec] = useState(0)
  const [arrivalDirty, setArrivalDirty] = useState(false)
  const [mode, setMode] = useState<Mode>('region')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [result, setResult] = useState<{ input: AlertPublishInput; resp: AlertPublishResponse; mode: Mode } | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  // S 波到达自动估算：参考点直线距离 ÷ S 波速（真实几何计算，可手动覆盖）
  const autoArrival = Math.round(haversineKm(lat, lon, REF_CITY.lat, REF_CITY.lon) / S_WAVE_KM_S)
  const effectiveArrival = arrivalDirty ? arrivalSec : autoArrival

  const input: AlertPublishInput = {
    level,
    magnitude,
    epicenterLat: lat,
    epicenterLon: lon,
    epicenterName,
    depth,
    estimatedArrivalSec: effectiveArrival,
  }

  const mutation = useMutation({
    mutationFn: (m: Mode) => (m === 'broadcast' ? broadcastAlert(input) : publishAlert(input)),
    onSuccess: (resp, m) => {
      setResult({ input, resp, mode: m })
      setConfirmOpen(false)
      setErrorMsg('')
    },
    onError: (err: Error) => {
      setErrorMsg(err.message)
      setConfirmOpen(false)
    },
  })

  const needsConfirm = level === 'TAKE_ACTION' || mode === 'broadcast'
  const dangerous = level === 'TAKE_ACTION'

  const submit = () => {
    if (needsConfirm) {
      setConfirmOpen(true)
      return
    }
    mutation.mutate(mode)
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
      {/* 左：选点地图 */}
      <div className="panel-glass rounded-xl p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Crosshair size={14} className="text-primary" /> 震中选点
          </h2>
          <span className="text-[10px] text-muted-foreground">点击地图选取震中 · 红圈为烈度估计范围</span>
        </div>
        <PickMap
          lat={lat}
          lon={lon}
          magnitude={magnitude}
          onPick={(pLat, pLon) => {
            setLat(pLat)
            setLon(pLon)
            // 靠近参考城市时自动带出名称，减少手输
            const near = REF_CITY
            if (haversineKm(pLat, pLon, near.lat, near.lon) < 30) setEpicenterName(near.name)
          }}
        />
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground">城市快选：</span>
          {[
            { name: '乌鲁木齐', lat: 43.8256, lon: 87.6168 },
            { name: '昌吉', lat: 44.0146, lon: 87.3082 },
            { name: '石河子', lat: 44.3061, lon: 86.0806 },
            { name: '库尔勒', lat: 41.7661, lon: 86.1414 },
            { name: '喀什', lat: 39.4704, lon: 75.9897 },
          ].map((c) => (
            <button
              key={c.name}
              onClick={() => {
                setLat(c.lat)
                setLon(c.lon)
                setEpicenterName(c.name)
                setArrivalDirty(false)
              }}
              className={cn(
                'rounded border px-2 py-0.5 text-[11px] transition',
                epicenterName === c.name
                  ? 'border-primary/60 bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
              )}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* 右：参数与下发 */}
      <div className="flex flex-col gap-4">
        <div className="panel-glass rounded-xl p-4">
          <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Radio size={14} className="text-primary" /> 预警参数
          </h2>

          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">预警级别</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                {(['BE_AWARE', 'TAKE_ACTION'] as Level[]).map((l) => (
                  <button
                    key={l}
                    onClick={() => setLevel(l)}
                    className={cn(
                      'rounded-md border px-2 py-1.5 font-mono text-[11px] transition',
                      level === l
                        ? l === 'TAKE_ACTION'
                          ? 'border-critical bg-critical-soft text-critical'
                          : 'border-warning bg-warning-soft text-warning'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {l === 'TAKE_ACTION' ? 'TAKE_ACTION · 强制' : 'BE_AWARE · 提示'}
                  </button>
                ))}
              </div>
            </div>

            <RangeRow
              label="震级 M"
              value={magnitude}
              display={magnitude.toFixed(1)}
              min={1}
              max={9}
              step={0.1}
              onChange={setMagnitude}
            />

            <RangeRow
              label="震源深度 km"
              value={depth}
              display={String(depth)}
              min={0}
              max={100}
              step={1}
              onChange={setDepth}
            />

            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <Label className="text-xs text-muted-foreground">纬度</Label>
                <Input
                  aria-label="震中纬度"
                  className="mt-1 font-mono text-xs"
                  inputMode="decimal"
                  value={lat}
                  onChange={(e) => setLat(Number(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">经度</Label>
                <Input
                  aria-label="震中经度"
                  className="mt-1 font-mono text-xs"
                  inputMode="decimal"
                  value={lon}
                  onChange={(e) => setLon(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">震中名称</Label>
              <Input
                aria-label="震中名称"
                className="mt-1 text-xs"
                value={epicenterName}
                onChange={(e) => setEpicenterName(e.target.value)}
                placeholder="如：乌鲁木齐"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">S 波预计到达（秒）</Label>
                {!arrivalDirty && (
                  <span className="font-mono text-[10px] text-muted-foreground/70">自动 · 距{REF_CITY.name}</span>
                )}
              </div>
              <Input
                aria-label="S 波预计到达秒数"
                className="mt-1 font-mono text-xs"
                inputMode="numeric"
                value={effectiveArrival}
                onChange={(e) => {
                  setArrivalDirty(true)
                  setArrivalSec(Number(e.target.value) || 0)
                }}
              />
              <p className="mt-1 font-mono text-[10px] text-muted-foreground/70">
                距{REF_CITY.name} {Math.round(haversineKm(lat, lon, REF_CITY.lat, REF_CITY.lon))}km ÷ {S_WAVE_KM_S}km/s ≈ {autoArrival}s
              </p>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">发布方式</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                {([
                  { key: 'region', label: '区域发布' },
                  { key: 'broadcast', label: '全量广播' },
                ] as Array<{ key: Mode; label: string }>).map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMode(m.key)}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-[11px] transition',
                      mode === m.key
                        ? 'border-primary/60 bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {mode === 'broadcast' && (
                <p className="mt-1.5 text-[10px] leading-4 text-warning">
                  全量广播不经 Geohash 过滤，所有在线终端（含手机端）都会收到。
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 border-t border-border pt-3">
            <Button onClick={submit} disabled={mutation.isPending} className="w-full gap-1.5 text-xs">
              <Send size={13} />
              {mutation.isPending ? '下发中…' : `下发预警（${mode === 'broadcast' ? '广播' : '区域'} · ${level === 'TAKE_ACTION' ? '强制' : '提示'}）`}
            </Button>
            {dangerous && (
              <p className="mt-2 flex items-start gap-1 text-[10px] leading-4 text-critical">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                TAKE_ACTION 会在所有接收终端触发全屏强制警报与警笛，请确认处于调试场景。
              </p>
            )}
            {errorMsg && <p className="mt-2 text-[10px] text-critical">下发失败：{errorMsg}</p>}
          </div>
        </div>

        {result && (
          <div className="panel-glass rounded-xl p-4">
            <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <MapPin size={14} className="text-success" /> 下发成功
            </h2>
            <dl className="space-y-1 font-mono text-[11px]">
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">topic</dt><dd className="truncate text-foreground">{result.resp.topic}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">message_id</dt><dd className="truncate text-foreground">{result.resp.message_id}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">geohash</dt><dd className="text-foreground">{result.resp.geohash}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">qos</dt><dd className="text-foreground">{result.resp.qos}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">M / 深度</dt><dd className="text-foreground">M{result.input.magnitude.toFixed(1)} / {result.input.depth}km</dd></div>
            </dl>
            <p className="mt-2 border-t border-border pt-2 text-[10px] leading-4 text-muted-foreground">
              预警已进入 MQTT 链路：端上（App/观测站）与指挥屏将实时呈现，波传播动画由 alert 推送驱动。
            </p>
          </div>
        )}
      </div>

      {/* 二次确认 */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle size={16} className="text-critical" />
              确认下发预警
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-xs leading-5">
                <p>
                  即将以
                  <Badge variant="outline" className="mx-1 border-critical/60 font-mono text-critical">{level}</Badge>
                  {mode === 'broadcast' ? '全量广播' : '区域发布'}方式下发调试预警：
                </p>
                <ul className="space-y-0.5 font-mono text-[11px] text-foreground">
                  <li>震中 {epicenterName || '未命名'} ({lat.toFixed(4)}, {lon.toFixed(4)})</li>
                  <li>M{magnitude.toFixed(1)} · 深度 {depth}km · S 波约 {effectiveArrival}s</li>
                </ul>
                {dangerous && (
                  <p className="text-critical">
                    TAKE_ACTION 将在接收终端触发全屏强制警报与警笛。此操作经由 alerts.publish 权限校验并记录。
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="text-xs">取消</Button>
            <Button
              variant={dangerous ? 'destructive' : 'default'}
              onClick={() => mutation.mutate(mode)}
              disabled={mutation.isPending}
              className="text-xs"
            >
              {mutation.isPending ? '下发中…' : '确认下发'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
